import React, { useContext } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { LogoFull } from "./brand/Logo";
import {
  LayoutDashboard,
  FileText,
  CreditCard,
  Bell,
  User,
  LogOut,
  ScrollText,
} from "lucide-react";

const NAV = [
  { icon: LayoutDashboard, label: "Overview",   to: "/resident",              end: true,  disabled: false },
  { icon: FileText,        label: "My Bills",   to: "/resident/bills",        end: false, disabled: false },
  { icon: CreditCard,      label: "Payments",   to: "/resident/payments",     end: false, disabled: false },
  { icon: ScrollText,      label: "Statements", to: "/resident/statements",   end: false, disabled: false },
  { icon: Bell,            label: "Notices",    to: "/resident/notices",      end: false, disabled: false },
  { icon: User,            label: "Profile",    to: "/resident/profile",      end: false, disabled: false },
] as const;

const getInitials = (name: string) => {
  const p = name.trim().split(/\s+/);
  return p.length >= 2
    ? (p[0][0] + p[p.length - 1][0]).toUpperCase()
    : p[0].slice(0, 2).toUpperCase();
};

interface Props {
  onClose?: () => void;
}

const ResidentSidebar: React.FC<Props> = ({ onClose }) => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const initials = user?.name ? getInitials(user.name) : "R";

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="w-60 h-full flex flex-col bg-white border-r border-slate-100">
      {/* Brand */}
      <div className="px-5 py-5">
        <div className="flex flex-col gap-2">
          <LogoFull light={false} size="md" className="self-start" />
          <div className="inline-flex px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium self-start">
            Resident Portal
          </div>
        </div>
      </div>

      {/* Nav items */}
      <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
        {NAV.map(({ icon: Icon, label, to, end, disabled }) =>
          disabled ? (
            <div
              key={label}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-300 cursor-not-allowed select-none"
            >
              <Icon size={16} />
              <span className="text-sm font-semibold">{label}</span>
              <span className="ml-auto text-[9px] bg-slate-50 text-slate-300 px-1.5 py-0.5 rounded font-bold uppercase tracking-wide">
                Soon
              </span>
            </div>
          ) : (
            <NavLink
              key={label}
              to={to}
              end={end}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-sm font-semibold ${
                  isActive
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={16} className={isActive ? "text-blue-600" : "text-slate-400"} />
                  {label}
                </>
              )}
            </NavLink>
          )
        )}
      </nav>

      {/* User strip */}
      <div className="px-3 py-4 border-t border-slate-50">
        <div className="flex items-center gap-2.5 px-2.5 py-2.5 rounded-xl bg-slate-50">
          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-semibold shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold text-slate-700 truncate">{user?.name || "Resident"}</div>
            <div className="text-[10px] text-slate-400 truncate">{user?.tenantName}</div>
          </div>
          <button
            onClick={handleLogout}
            title="Sign out"
            className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-all shrink-0"
          >
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResidentSidebar;
